import { useRive, useStateMachineInput } from '@rive-app/react-canvas';
import classNames from 'clsx';
import React, { type ForwardedRef, forwardRef, memo, useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { NestleFormWrapperContext } from 'reactApp/sections/Nestle/FormWrapper/FormWrapper.context';
import { NESTLE_HIDE_ON_EXPORT_CLASS } from 'reactApp/sections/Nestle/FormWrapper/FormWrapper.helpers';
import { animations, animationsMobile, backgroundImages, backgroundImagesMobile } from 'reactApp/sections/Nestle/PetsCard/Toy/Toy.data';

import styles from './Toy.css';

const RIVE_STATE_MACHINE_NAME = 'State Machine 3d elements';
const RIVE_TRIGGER_NAME = 'Trigger 1';

const animationFigures = ['bone', 'paw', 'can', 'mouse', 'bowl', 'drumstick'];

export const Toy = memo(
    forwardRef((_, ref: ForwardedRef<HTMLDivElement>) => {
        const [animationSrc, setAnimationSrc] = useState<string>('');
        const [animationSrcMobile, setAnimationSrcMobile] = useState<string>('');
        const [clicked, setIsClicked] = useState(false);
        const [animationIndex, setAnimationIndex] = useState(0);

        const { formData } = useContext(NestleFormWrapperContext);
        const { cardColor } = formData;

        const isMobile = useSelector(QuotaLandingSelectors.isMobile);

        const backgroundImage = backgroundImages[cardColor][animationFigures[animationIndex]];
        const backgroundImageMobile = backgroundImagesMobile[cardColor][animationFigures[animationIndex]];

        const { rive, RiveComponent } = useRive({
            src: animationSrc,
            stateMachines: RIVE_STATE_MACHINE_NAME,
            autoplay: true,
            shouldDisableRiveListeners: true,
        });

        const { rive: riveMobile, RiveComponent: RiveComponentMobile } = useRive({
            src: animationSrcMobile,
            stateMachines: RIVE_STATE_MACHINE_NAME,
            autoplay: true,
        });

        const triggerAnimation = useStateMachineInput(rive, RIVE_STATE_MACHINE_NAME, RIVE_TRIGGER_NAME);
        const triggerAnimationMobile = useStateMachineInput(riveMobile, RIVE_STATE_MACHINE_NAME, RIVE_TRIGGER_NAME);

        const handleClick = useCallback(() => {
            triggerAnimation?.fire();
            triggerAnimationMobile?.fire();
            setAnimationIndex((prev) => (prev === animationFigures.length - 1 ? 0 : prev + 1));
            if (!clicked) {
                setIsClicked(true);
            }
        }, [triggerAnimation, triggerAnimationMobile, setIsClicked, clicked, setAnimationIndex]);

        useEffect(() => {
            setAnimationIndex(0);
            setAnimationSrc(animations[cardColor]);
            setAnimationSrcMobile(animationsMobile[cardColor]);
        }, [cardColor, setAnimationSrc, setAnimationSrcMobile, setAnimationIndex]);

        return (
            <>
                <div
                    className={classNames(styles.root, {
                        [styles[`root_${cardColor}`]]: cardColor,
                        [styles[`root_${animationFigures[animationIndex]}`]]: animationFigures[animationIndex],
                    })}
                    onClick={handleClick}
                    role="button"
                    aria-label="Сменить картинку"
                >
                    <img src={backgroundImage} alt="toy-background" className={styles.image} />
                    <img src={backgroundImageMobile} alt="toy-background" className={styles.imageMobile} />
                    <div className={styles.animationDesktop}>
                        <RiveComponent key={animationSrc} />
                    </div>
                    <div className={styles.animationMobile}>
                        <RiveComponentMobile key={animationSrcMobile} />
                    </div>
                    {isMobile && <div className={styles.mobileTrigger} />}
                </div>
                {!clicked &&
                    !isMobile &&
                    ref &&
                    'current' in ref &&
                    ref?.current &&
                    createPortal(
                        <div className={classNames(styles.clickLabel)}>
                            <svg
                                className={NESTLE_HIDE_ON_EXPORT_CLASS}
                                xmlns="http://www.w3.org/2000/svg"
                                width="83"
                                height="89"
                                viewBox="0 0 83 89"
                                fill="none"
                            >
                                <path
                                    d="M2.2453 71.755C36.7224 85.8989 55.3153 68.1549 53.8015 41.8628"
                                    stroke="#303030"
                                    strokeWidth="2.6"
                                />
                                <path
                                    d="M14.9238 67.624C8.82583 70.6151 2.48251 71.2918 2.48251 71.2918C2.48251 71.2918 7.37212 78.7444 7.52549 84.5137"
                                    stroke="#303030"
                                    strokeWidth="2.6"
                                />
                                <path
                                    d="M50.7317 13.1567L56.8306 25.6699L61.8296 13.6657"
                                    stroke="#303030"
                                    strokeWidth="2.6"
                                    strokeLinejoin="round"
                                />
                                <path d="M60.1537 9.10791L66.9595 23.1732" stroke="#303030" strokeWidth="2.6" />
                                <path
                                    d="M41.7794 34.1784L41.0233 18.6064L52.8944 29.5249"
                                    stroke="#303030"
                                    strokeWidth="2.6"
                                    strokeLinejoin="round"
                                />
                                <path d="M18.037 21.1162L25.9856 42.9032" stroke="#303030" strokeWidth="2.6" />
                                <path d="M65.5985 6.38525L73.0645 21.0257" stroke="#303030" strokeWidth="2.6" />
                                <path d="M30.2222 16.5764L23.3059 31.8145L39.2829 35.4234" stroke="#303030" strokeWidth="2.6" />
                                <path d="M74.6729 3.20943L70.4142 14.0164L82.1389 16.035" stroke="#303030" strokeWidth="2.6" />
                            </svg>
                        </div>,
                        ref.current
                    )}
            </>
        );
    })
);

Toy.displayName = 'Toy';
